import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Card from '@/shared/Card'

const STAGES = ['', 'Announced', 'PreProduction', 'Production', 'PostProduction', 'Completed']

const CastMember = ({ member }) => {
  return (
    <a href={`/m/${member.username}`} className="flex items-center space-x-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
      <img className="h-8 w-8 rounded-sm" src={member.image_url} alt={member.display_name} />
      <div className="flex flex-col truncate">
        <span className="text-sm font-medium truncate">{member.display_name}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{member.credit_type}{member.role ? ` - ${member.role}` : ''}</span>
      </div>
    </a>
  )
}

const CreditResultRow = ({ credit }) => {
  const [showAllCast, setShowAllCast] = useState(false)
  const cast = credit.cast || []
  const visibleCast = showAllCast ? cast : cast.slice(0, 5)

  return (
    <li className="bg-gray-50 dark:bg-gray-900 p-3 shadow sm:rounded-lg">
      <div className="flex gap-x-3">
        <a href={`/credits/${credit.slug}`} className="flex-shrink-0">
          <img className="h-20 w-20 rounded-sm" src={credit.imageUrl} alt={credit.name} />
        </a>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="truncate">
              <a href={`/credits/${credit.slug}`} className="text-lg font-medium hover:text-cccblue">{credit.name}</a>
              {credit.parentName && <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{credit.parentName}</div>}
            </div>
            <a href={`/credits/${credit.slug}`} className="flex-shrink-0 ml-2 px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white dark:bg-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800">
              View
            </a>
          </div>
          <div className="flex gap-x-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
            {credit.year && <span>Year: {credit.year}</span>}
            {credit.stage && <span className="px-2 py-0.5 rounded-full bg-cccblue bg-opacity-10 text-cccblue">{credit.stage}</span>}
            <span>{credit.userCreditsCount} cast member{credit.userCreditsCount !== 1 ? 's' : ''}</span>
          </div>

          {cast.length > 0 && (
            <div className="mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                {visibleCast.map((member, idx) => (
                  <CastMember key={`${credit.id}-${idx}`} member={member} />
                ))}
              </div>
              {cast.length > 5 && !showAllCast && (
                <button onClick={() => setShowAllCast(true)} className="mt-1 text-sm text-cccblue hover:underline">
                  Show {cast.length - 5} more cast member(s)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

const CreditSearch = () => {
  const urlParams = new window.URLSearchParams(window.location.search)
  const [searchValue, setSearchValue] = useState(urlParams.get('search') || '')
  const [year, setYear] = useState(urlParams.get('year') || '')
  const [stage, setStage] = useState(urlParams.get('stage') || '')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const { getRequest, createQueryString } = useQuery()
  const history = useHistory()

  const fetchData = () => {
    if (searchValue.trim().length === 0 && !year && !stage) return

    setLoading(true)
    const data = { search: searchValue || '*', limit: 50 }
    if (year) data.year = year
    if (stage) data.stage = stage

    getRequest('/api/v3/omnisearch/credits', data, (err, jsonData) => {
      setLoading(false)
      if (err) return
      setResults(jsonData.results)
    })
  }

  const updateUrl = () => {
    const params = {}
    if (searchValue) params.search = searchValue
    if (year) params.year = year
    if (stage) params.stage = stage
    history.push(`/credits/search?${createQueryString(params)}`)
  }

  useEffect(() => { fetchData() }, [])
  useDebouncedEffect(() => { updateUrl(); fetchData() }, 300, [searchValue])
  useDebouncedEffect(() => { updateUrl(); fetchData() }, 200, [year, stage])

  return (
    <div className="max-w-6xl w-full">
      <Card title="Search Credits">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Name</label>
            <input
              type="text"
              className="mt-1 block w-full"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search by credit or series name..."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Year</label>
            <input
              type="number"
              className="mt-1 block w-full"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="e.g. 2025"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Stage</label>
            <select
              className="mt-1 block w-full"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
            >
              <option value="">All Stages</option>
              {STAGES.filter(Boolean).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && <Loading />}

        {!loading && results.length === 0 && (searchValue || year || stage) && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No results found.</p>
        )}

        {!loading && results.length > 0 && (
          <ul className="space-y-3">
            {results.map((credit) => (
              <CreditResultRow key={credit.id} credit={credit} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

export default CreditSearch

CreditSearch.propTypes = {}
